import { useCallback, useEffect } from 'react'
import { logEvent } from 'firebase/analytics'
import { getAnalyticsInstance, auth } from '@/lib/firebase'
import { usePostHog } from 'posthog-js/react'
import { FirebaseAnalytics } from '@capacitor-firebase/analytics'
import { Posthog as PosthogCapacitor } from '@capawesome/capacitor-posthog'
import { Capacitor } from '@capacitor/core'
import {
  ANALYTICS_EVENT_DEFINITIONS,
  type AnalyticsEventName,
  type AnalyticsEventPayloads,
  type AnalyticsEventPayloadsWithSession,
} from '@/analytics/analyticsEvents'
import { isAnalyticsDebugEnabled, shouldTrackAnalytics } from '@/analytics/analyticsConfig'
import {
  type AnalyticsSession,
  getSessionStateForEvent,
  reserveSessionStartSend,
} from '@/analytics/analyticsSession'
import { sendMetaEvent } from '@/lib/metaEvents'

const cleanParams = (params: Record<string, unknown>) =>
  Object.fromEntries(Object.entries(params).filter(([, value]) => value !== undefined))

export const useAnalytics = () => {
  const posthog = usePostHog()

  const sendEvent = useCallback(
    async <K extends AnalyticsEventName>(
      eventName: K,
      parameters: AnalyticsEventPayloadsWithSession[K],
      session?: AnalyticsSession | null
    ) => {
      if (!shouldTrackAnalytics()) {
        if (isAnalyticsDebugEnabled()) {
          console.log('[Analytics] Skipped (no consent)', eventName, parameters)
        }
        return
      }

      const definition = ANALYTICS_EVENT_DEFINITIONS[eventName]
      if (!definition) {
        if (isAnalyticsDebugEnabled()) {
          console.warn('[Analytics] Unknown event:', eventName)
        }
        return
      }

      const baseParams = parameters as Record<string, unknown>
      const userId = auth.currentUser?.uid
      const params = cleanParams({
        ...baseParams,
        ...(session && !('session_id' in baseParams) ? { session_id: session.id } : {}),
        ...(userId && !('user_id' in baseParams) ? { user_id: userId } : {}),
      })

      if (isAnalyticsDebugEnabled()) {
        console.log('[Analytics] Event:', eventName, params)
      }

      const shouldSendGA = definition.destinations.includes('ga')
      const shouldSendPosthog = definition.destinations.includes('posthog')
      const shouldSendMeta = definition.destinations.includes('meta')

      if (shouldSendGA) {
        try {
          if (Capacitor.isNativePlatform()) {
            await FirebaseAnalytics.logEvent({
              name: eventName,
              params,
            })
          } else {
            const analytics = await getAnalyticsInstance()
            if (analytics) {
              logEvent(analytics, eventName as string, params)
            }
          }
        } catch (error) {
          console.error(`[Analytics] GA event failed: ${eventName}`, error)
        }
      }

      if (shouldSendPosthog) {
        try {
          if (Capacitor.isNativePlatform()) {
            await PosthogCapacitor.capture({
              event: eventName,
              properties: params,
            })
          } else if (posthog) {
            posthog.capture(eventName, params)
          }
        } catch (error) {
          console.error(`[Analytics] PostHog event failed: ${eventName}`, error)
        }
      }

      if (shouldSendMeta) {
        await sendMetaEvent(eventName as string, params)
      }
    },
    [posthog]
  )

  const ensureSessionStart = useCallback(async (session: AnalyticsSession | null) => {
    if (!session) return
    if (!reserveSessionStartSend(session)) return
    await sendEvent('session_start', {
      session_start_time: session.startTime,
    }, session)
  }, [sendEvent])

  const sendSessionEnd = useCallback(async (session: AnalyticsSession | null) => {
    if (!session) return
    await sendEvent('session_end', {
      session_duration: Math.round((session.lastActivity - session.startTime) / 1000),
    }, session)
  }, [sendEvent])

  const trackEvent = useCallback(
    async <K extends AnalyticsEventName>(
      eventName: K,
      parameters: AnalyticsEventPayloads[K]
    ) => {
      if (!shouldTrackAnalytics()) {
        if (isAnalyticsDebugEnabled()) {
          console.log('[Analytics] Skipped (no consent)', eventName, parameters)
        }
        return
      }

      const { session, endedSession } = getSessionStateForEvent()
      await sendSessionEnd(endedSession)

      // Ensure session start is captured once consent exists
      if (eventName !== 'session_start' && eventName !== 'session_end') {
        await ensureSessionStart(session)
      }

      await sendEvent(eventName, parameters as AnalyticsEventPayloadsWithSession[K], session)
    },
    [ensureSessionStart, sendEvent, sendSessionEnd]
  )

  useEffect(() => {
    if (!shouldTrackAnalytics()) return

    const { session, endedSession } = getSessionStateForEvent()
    void sendSessionEnd(endedSession)
    void ensureSessionStart(session)
  }, [ensureSessionStart, sendSessionEnd])

  const trackPageView = useCallback(
    async (pagePath: string, pageTitle?: string) => {
      const pageViewParams = {
        page_title: pageTitle || pagePath,
        page_location: typeof window !== 'undefined' ? window.location.href : undefined,
        page_path: pagePath,
      }

      await trackEvent('page_view', pageViewParams)
    },
    [trackEvent]
  )

  const trackAuthPrompt = useCallback(
    async (source: string, reason?: string) => {
      await trackEvent('auth_prompt_shown', { source, reason })
    },
    [trackEvent]
  )

  const trackAuthDismiss = useCallback(
    async (source: string, reason?: string, dismissType?: string) => {
      await trackEvent('auth_prompt_dismissed', {
        source,
        reason,
        dismiss_type: dismissType,
      })
    },
    [trackEvent]
  )

  const trackSignIn = useCallback(
    async (method: string = 'email', source?: string) => {
      await trackEvent('auth_login_success', { method, source })
    },
    [trackEvent]
  )

  const trackSignUp = useCallback(
    async (method: string = 'email', source?: string, hasInstagram?: boolean) => {
      await trackEvent('auth_signup_success', {
        method,
        source,
        has_instagram: hasInstagram,
      })
    },
    [trackEvent]
  )

  return {
    trackEvent,
    trackPageView,
    trackAuthPrompt,
    trackAuthDismiss,
    trackSignIn,
    trackSignUp,
  }
}
